import { useRole } from "../../../contexts/RoleContext";
import { getMailsByRole, npcContacts, type NpcContact } from "../../../mocks/inbox";

interface Props {
  npcId: string;
  onClose: () => void;
  onSelectMail: (mailId: string) => void;
  onOpenNpcChat: (npcChatId: string) => void;
}

// 거래처 → NPC채팅 매핑
const NPC_CHAT_MAP: Record<string, string> = {
  npc_mkt2: "journalist",
  npc_leg2: "investor",
  npc_leg1: "legal",
  npc_ar1: "mentor",
  npc_ar2: "mentor",
  npc_fin1: "mentor",
  npc_fin2: "investor",
  npc_mkt1: "rival",
};

const statusCfg = {
  urgent: { label: "긴급", cls: "bg-red-500/15 text-red-400/90 border-red-500/20" },
  "action-needed": { label: "조치 필요", cls: "bg-amber-500/15 text-amber-400/90 border-amber-500/20" },
  ongoing: { label: "진행 중", cls: "bg-[#4B6CF7]/15 text-[#7A9BFF] border-[#4B6CF7]/20" },
  waiting: { label: "대기", cls: "bg-white/[0.05] text-[#555555] border-white/[0.08]" },
};

export default function ContactDrawer({ npcId, onClose, onSelectMail, onOpenNpcChat }: Props) {
  const { jobRole } = useRole();
  const npc = npcContacts.find(n => n.id === npcId);
  const allMails = getMailsByRole(jobRole);
  const npcMails = allMails.filter(m => m.npcId === npcId);
  const chatId = NPC_CHAT_MAP[npcId] ?? "mentor";

  if (!npc) return null;

  return (
    <>
      {/* 배경 오버레이 (반투명) */}
      <div
        className="absolute inset-0 z-10"
        onClick={onClose}
      />

      {/* 드로어 */}
      <div
        className="absolute right-0 top-0 bottom-0 z-20 flex flex-col bg-[#0F0F11] border-l border-white/[0.08]"
        style={{ width: 240, animation: "slideInRight 0.18s ease" }}
      >
        {/* 헤더 */}
        <div className="px-4 py-4 border-b border-white/[0.06] flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="relative shrink-0">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/[0.07] text-[#CCCCCC] text-sm font-bold border border-white/[0.08]">
                {npc.avatar}
              </div>
              <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#0F0F11] ${npc.isOnline ? "bg-[#4B6CF7]" : "bg-[#333333]"}`} />
            </div>
            <div className="min-w-0">
              <p className="text-[#F2F2F2] text-xs font-semibold leading-tight">{npc.name}</p>
              <p className="text-[#555555] text-[9px] mt-0.5 leading-snug">{npc.role}</p>
              <p className="text-[#666666] text-[9px]">{npc.company}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-[#333333] hover:text-[#888888] cursor-pointer transition-colors shrink-0">
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* 상태 + 성향 */}
        <div className="px-4 py-3 border-b border-white/[0.05]">
          <div className="flex items-center gap-1.5 mb-2">
            <div className={`w-1.5 h-1.5 rounded-full ${npc.isOnline ? "bg-[#4B6CF7]" : "bg-[#333333]"}`}></div>
            <span className="text-[10px] text-[#555555]">{npc.isOnline ? "온라인" : "오프라인"}</span>
          </div>
          <div className="bg-white/[0.03] border border-white/[0.05] rounded-lg px-3 py-2 mb-2">
            <div className="flex items-center gap-1.5 mb-1">
              <i className="ri-psychology-line text-[#D97706]/70 text-xs"></i>
              <span className="text-[9px] text-[#D97706]/60 font-semibold uppercase tracking-wider">성향</span>
            </div>
            <p className="text-[#777777] text-[10px] leading-relaxed">{npc.persona}</p>
          </div>
          <p className="text-[#444444] text-[10px] leading-relaxed">{npc.description}</p>
        </div>

        {/* 관련 메일 */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 pt-3 pb-1">
            <p className="text-[#666666] text-[9px] uppercase tracking-widest">관련 메일 ({npcMails.length})</p>
          </div>
          {npcMails.length === 0 ? (
            <div className="px-4 py-3 text-[#666666] text-[10px]">관련 메일이 없습니다.</div>
          ) : (
            <div className="px-3 space-y-1.5 pb-3">
              {npcMails.map(mail => {
                const sc = statusCfg[mail.status as keyof typeof statusCfg];
                return (
                  <button
                    key={mail.id}
                    type="button"
                    onClick={() => { onSelectMail(mail.id); onClose(); }}
                    className="w-full text-left p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.05] hover:border-white/[0.08] transition-all cursor-pointer group"
                  >
                    <div className="flex items-start justify-between gap-1 mb-1">
                      <p className="text-[#AAAAAA] text-[10px] font-medium leading-snug group-hover:text-[#CCCCCC] transition-colors line-clamp-2 flex-1">{mail.subject}</p>
                      {!mail.isRead && <div className="w-1.5 h-1.5 rounded-full bg-[#4B6CF7] shrink-0 mt-1"></div>}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-[8px] px-1.5 py-0.5 rounded border ${sc?.cls}`}>{sc?.label}</span>
                      <span className="text-[#666666] text-[9px]">{mail.date.split(" ")[0]}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 액션 버튼 */}
        <div className="px-3 py-3 border-t border-white/[0.05] space-y-1.5">
          <button
            type="button"
            onClick={() => onOpenNpcChat(chatId)}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#D97706]/12 to-[#EA580C]/12 border border-[#D97706]/20 hover:from-[#D97706]/20 hover:to-[#EA580C]/20 transition-all cursor-pointer group"
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-robot-2-line text-[#D97706]/80 text-sm"></i>
            </div>
            <div className="flex-1 text-left">
              <div className="text-[#D97706]/80 text-[10px] font-semibold leading-tight">AI NPC와 협상 연습</div>
              <div className="text-[#555555] text-[9px]">유사 캐릭터로 대화 시작</div>
            </div>
            <i className="ri-arrow-right-line text-[#D97706]/40 text-xs group-hover:text-[#D97706]/70 transition-colors"></i>
          </button>

          <div className="text-center">
            <p className="text-[#666666] text-[9px]">위 메일 목록을 클릭하면 해당 메일로 이동합니다</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}
